import React from 'react';
import gql from 'graphql-tag';
import Cookies from 'js-cookie';
import apollo from '../../plugins/gatsby-plugin-top-layout/apollo';
import { determineCurrentCustomDomain } from "./custom_domains";
import BrowserUtil from '../util/BrowserUtil';

const SESSION_STORAGE_KEY = 'dubbotSession';
const SSO_COOKIE_NAME = 'sso-good';

const LogoutMutation = gql`
  mutation LogoutMutation {
    signOut {
      status
    }
  }
`;

/**
 * Helper function to dispatch storage event for cross-tab sync and React hook updates
 */
const dispatchStorageEvent = (sessionValue) => {
  if (BrowserUtil.isBrowser() && window.dispatchEvent) {
    window.dispatchEvent(new StorageEvent('storage', {
      key: SESSION_STORAGE_KEY,
      newValue: JSON.stringify(sessionValue),
      storageArea: localStorage,
    }));
  }
};

/**
 * Session storage layer - handles localStorage and SSO cookie management
 */
const SessionStorage = {
  /**
   * Check if session should be considered expired (SSO accounts)
   */
  isExpired() {
    return determineCurrentCustomDomain() && !Cookies.get(SSO_COOKIE_NAME);
  },

  /**
   * Get session from storage
   * Returns empty object if browser check fails or session expired
   */
  get() {
    if (!BrowserUtil.isBrowser() || this.isExpired()) {
      return {};
    }
    return BrowserUtil.getItemFromLocalStorage(SESSION_STORAGE_KEY) || {};
  },

  /**
   * Save session to storage
   */
  set(session) {
    if (determineCurrentCustomDomain()) {
      Cookies.set(SSO_COOKIE_NAME, 1, { sameSite: "Strict" });
    }
    BrowserUtil.setItemInLocalStorage(SESSION_STORAGE_KEY, session);
  },

  /**
   * Clear session from storage
   */
  clear() {
    BrowserUtil.setItemInLocalStorage(SESSION_STORAGE_KEY, {});
    Cookies.remove(SSO_COOKIE_NAME);
  },
};

/**
 * Context for session data
 */
export const SessionContext = React.createContext(null);

/**
 * Utility functions that work outside React context.
 * These use SessionStorage directly and can be used in Apollo Links, etc.
 * For React components, prefer useSession() hook for reactive updates.
 */

/**
 * Get current session (for usage outside of React)
 */
export const getSession = () => SessionStorage.get();

/**
 * Check if user is logged in (for usage outside of React)
 */
export const isLoggedIn = () => {
  const session = SessionStorage.get();
  return !!session?.accessToken;
};

/**
 * Update session with partial data (for usage outside of React)
 */
export const updateSession = (updates) => {
  const currentSession = SessionStorage.get();
  const updatedSession = { ...currentSession, ...updates };
  SessionStorage.set(updatedSession);
  dispatchStorageEvent(updatedSession);
  return updatedSession;
};

/**
 * Clear session (for usage outside of React)
 */
export const clearSession = () => {
  SessionStorage.clear();
  dispatchStorageEvent({});
};

/**
 * Handle login (for usage outside of React)
 */
export const handleLogin = ({
  accessToken,
  refreshToken,
  rememberMe,
  userId,
  userEmail,
  userName,
  avatarUrl,
  avatarColor,
  userSettings,
  isSuperUser,
  handshakeLogoutUrl,
}) => {
  const newSession = {
    accessToken,
    refreshToken,
    rememberMe,
    userId,
    userEmail,
    userName,
    avatarUrl,
    avatarColor,
    userSettings,
    isSuperUser,
    handshakeLogoutUrl,
  };
  SessionStorage.set(newSession);
  dispatchStorageEvent(newSession);
};

/**
 * Logout (for usage outside of React)
 * Returns a Promise that resolves after logout completes
 */
export const logout = async (callback) => {
  const session = SessionStorage.get();

  const callbackWrapper = () => {
    clearSession();
    callback?.(session);
  };

  return apollo
    .mutate({
      mutation: LogoutMutation,
      context: {
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
        },
      },
    })
    .then(callbackWrapper)
    .catch(callbackWrapper);
};

/**
 * Update access token (for usage outside of React)
 */
export const updateAccessToken = (accessToken) => {
  return updateSession({ accessToken });
};

/**
 * Update user setting in session (for usage outside of React)
 */
export const updateUserSettingInSession = (key, value) => {
  const session = SessionStorage.get();
  const userSettings = session.userSettings || {};

  return updateSession({
    userSettings: {
      ...userSettings,
      [key]: value,
    },
  });
};

/**
 * Sync user settings to session (for usage outside of React)
 */
export const syncUserSettingsToSession = (userSettings) => {
  if (userSettings) {
    return updateSession({ userSettings });
  }
};

/**
 * Check if user setting is enabled (for usage outside of React)
 */
export const isUserSettingEnabled = (key) => {
  const session = SessionStorage.get();
  return session.userSettings?.[key] === true;
};

/**
 * Hook to access session from context.
 * Must be used within SessionProvider.
 */
export function useSession() {
  const context = React.useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
}

/**
 * Provider that manages session state and provides session operations.
 *
 * Provides:
 * - session: Current session object
 * - isLoggedIn: Boolean indicating if user is logged in
 * - updateSession: Function to update session
 * - clearSession: Function to clear session
 * - handleLogin: Function to handle login
 * - logout: Function to logout
 * - updateAccessToken: Function to update access token
 * - updateUserSetting: Function to update user setting
 * - syncUserSettings: Function to sync user settings
 * - isUserSettingEnabled: Function to check if user setting is enabled
 *
 * @example
 * <SessionProvider>
 *   <YourApp />
 * </SessionProvider>
 */
export function SessionProvider({ children }) {
  const [session, setSessionState] = React.useState(() => SessionStorage.get());

  // Sync session state with storage when storage changes externally
  React.useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === SESSION_STORAGE_KEY) {
        setSessionState(SessionStorage.get());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const updateSessionInternal = React.useCallback((updates) => {
    const updatedSession = updateSession(updates);
    setSessionState(updatedSession);
    return updatedSession;
  }, []);

  const clearSessionInternal = React.useCallback(() => {
    clearSession();
    setSessionState({});
  }, []);

  const handleLoginInternal = React.useCallback((loginData) => {
    handleLogin(loginData);
    setSessionState(SessionStorage.get());
  }, []);

  const logoutInternal = React.useCallback((callback) => {
    logout(() => {
      setSessionState({});
      callback?.(session);
    });
  }, [session]);

  const updateAccessTokenInternal = React.useCallback((accessToken) => {
    const updatedSession = updateAccessToken(accessToken);
    setSessionState(updatedSession);
    return updatedSession;
  }, []);

  const updateUserSettingInternal = React.useCallback((key, value) => {
    const updatedSession = updateUserSettingInSession(key, value);
    setSessionState(updatedSession);
    return updatedSession;
  }, []);

  const syncUserSettingsInternal = React.useCallback((userSettings) => {
    const updatedSession = syncUserSettingsToSession(userSettings);
    if (updatedSession) {
      setSessionState(updatedSession);
    }
    return updatedSession;
  }, []);

  const isUserSettingEnabledInternal = React.useCallback((key) => {
    return isUserSettingEnabled(key);
  }, []);

  const isLoggedIn = React.useMemo(() => {
    return !!session?.accessToken;
  }, [session?.accessToken]);

  const value = React.useMemo(() => ({
    session,
    isLoggedIn,
    updateSession: updateSessionInternal,
    clearSession: clearSessionInternal,
    handleLogin: handleLoginInternal,
    logout: logoutInternal,
    updateAccessToken: updateAccessTokenInternal,
    updateUserSetting: updateUserSettingInternal,
    syncUserSettings: syncUserSettingsInternal,
    isUserSettingEnabled: isUserSettingEnabledInternal,
  }), [
    session,
    isLoggedIn,
    updateSessionInternal,
    clearSessionInternal,
    handleLoginInternal,
    logoutInternal,
    updateAccessTokenInternal,
    updateUserSettingInternal,
    syncUserSettingsInternal,
    isUserSettingEnabledInternal,
  ]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

